import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Users, Target, Shield, TrendingDown, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fadeUp = {
 initial: { opacity: 0, y: 30 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.6 },
};

const howWeHelp = [
 {
  icon: Zap,
  title: "Build fast. but only what matters",
  description: "Speed without direction is just expensive chaos. We help you identify the smallest thing you can build to learn the most.",
 },
 {
  icon: Users,
  title: "Get real customers early",
  description: "Before you build features, we help you find people willing to pay. Real demand validation, not surveys.",
 },
 {
  icon: Target,
  title: "Validate demand and pricing",
  description: "Will people pay? How much? We help you answer these questions before you have invested too much.",
 },
 {
  icon: TrendingDown,
  title: "Reduce burn",
  description: "Every dollar you do not spend on the wrong thing is a dollar you can spend on the right thing. We help you stay lean.",
 },
 {
  icon: Shield,
  title: "Remove operational overload",
  description: "Founders should not be drowning in execution. We take on the work that is slowing you down.",
 },
 {
  icon: Compass,
  title: "Support scale. responsibly",
  description: "When it is time to grow, we help you do it without losing what made you successful or burning out.",
 },
];

// Two people conversation illustration
const illustration = "/images/how-we-help.png";

export default function HowWeHelpPage() {
 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       How We Help
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       No frameworks. No buzzwords. Just practical support for founders who need to move fast without moving blind.
      </p>
     </motion.div>
    </div>
   </section>

   {/* The Difference - With Illustration */}
   <section className="bg-background py-16 sm:py-20 lg:py-24">
    <div className="container-wide">
     <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      {/* Illustration - Two people conversation */}
      <motion.div
       initial={{ opacity: 0, x: -30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7 }}
       className="flex justify-center"
      >
       <div className="w-full max-w-sm">
        <img
         src={illustration}
         alt="Founder-to-founder partnership"
         className="w-full h-auto"
        />
       </div>
      </motion.div>

      {/* Content */}
      <motion.div
       initial={{ opacity: 0, x: 30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7, delay: 0.1 }}
      >
       <h2 className="heading-section text-foreground mb-8">What Makes Us Different</h2>
       
       <div className="space-y-5">
        <div className="bg-card border border-border/50 rounded-xl p-6">
         <h3 className="font-serif text-lg font-medium text-foreground mb-2">We are not an agency.</h3>
         <p className="text-muted-foreground">
          Agencies sell deliverables. We care about outcomes. Our success is measured by your progress, not our invoices.
         </p>
        </div>
        
        <div className="bg-card border border-border/50 rounded-xl p-6">
         <h3 className="font-serif text-lg font-medium text-foreground mb-2">We are not consultants.</h3>
         <p className="text-muted-foreground">
          Consultants give advice and leave. We work alongside you. We stay in the weeds. We are accountable to results.
         </p>
        </div>
        
        <div className="bg-card border border-border/50 rounded-xl p-6">
         <h3 className="font-serif text-lg font-medium text-foreground mb-2">We are founders helping founders.</h3>
         <p className="text-muted-foreground">
          We have been where you are. We understand the stakes. We know what it feels like to carry the weight. and we are here to help lighten it.
         </p>
        </div>
       </div>
      </motion.div>
     </div>
    </div>
   </section>

   {/* Core Approach */}
   <section className="bg-section py-16 sm:py-20 lg:py-24">
    <div className="container-base">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
      className="text-center mb-12"
     >
      <h2 className="heading-large text-foreground mb-5">Our Approach</h2>
      <p className="body-base text-muted-foreground max-w-2xl mx-auto">
       We stay close to execution. We help you make decisions with confidence. We move when you move.
      </p>
     </motion.div>

     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {howWeHelp.map((item, i) => (
       <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: i * 0.1 }}
       >
        <Card className="card-elevated h-full">
         <CardContent className="p-6">
          <div className="icon-container mb-4">
           <item.icon className="h-5 w-5 text-accent" />
          </div>
          <h3 className="font-serif text-lg font-medium text-foreground mb-2">{item.title}</h3>
          <p className="text-muted-foreground text-sm">{item.description}</p>
         </CardContent>
        </Card>
       </motion.div>
      ))}
     </div>
    </div>
   </section>

   {/* CTA */}
   <section className="bg-primary text-primary-foreground py-16 sm:py-20">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
     >
      <h2 className="heading-section mb-5">Ready to Move Forward?</h2>
      <p className="body-base text-primary-foreground/80 mb-6">
       Tell us where you are and where you want to go. We will show you how we can help.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
       <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
        <Link to="/contact">
         Submit Your Wishlist
         <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
       </Button>
       <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
        <Link to="/process">See Our Process</Link>
       </Button>
      </div>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
